import { useSelector } from 'react-redux';
import {
  FREESTAR_ENABLED,
  FREESTAR_PRIMIS_VIDEO_ENABLED,
  OUTSTREAM_VIDEO_AD_ENABLED,
} from 'src/common/constants/experiments/ads';
import useExperiment from 'src/common/hooks/useExperiment';
import { useRouteProps } from 'src/common/providers/RoutePropsProvider';
import { selectIsNowPlayingDialogOpen } from 'src/common/selectors/dialog';
import { useStores } from 'src/common/stores';
import { useDetermineAdEligibility } from 'src/common/utils/ads/determineAdEligibility';
import determineSponsorshipEnabled from 'src/common/utils/ads/determineSponsorshipEnabled';
import FreeStarVideoAdContainer from './FreeStarVideoAdContainer';
import { OutstreamVideoAd } from './OutstreamVideoAd';

interface OustreamVideoAdSelectorProps {
  id: string;
  isInNowPlayingDialog?: boolean;
  center?: boolean;
  /**
   * Renders the slot's empty box without loading an ad. Lets a server-rendered page reserve the
   * slot's space before the client knows whether it shows.
   */
  isPlaceholder?: boolean;
}

export function OutstreamVideoAdSelector(props: OustreamVideoAdSelectorProps) {
  const { playerStore } = useStores();
  const { id, isInNowPlayingDialog = false, center = false, isPlaceholder = false } = props;
  const { canShowNowPlayingMrecVideoAds = true } = playerStore.metadata()?.ads ?? {};
  const isNowPlayingDialogOpen = useSelector(selectIsNowPlayingDialogOpen);
  const guideId = useRouteProps().guideContext.guideId;
  const sponsorshipEnabled = useSelector(determineSponsorshipEnabled(guideId));
  const canShowAds = useDetermineAdEligibility(guideId);
  const isFreestarEnabled = useExperiment(FREESTAR_ENABLED);
  const isFreestarPrimisVideoEnabled = useExperiment(FREESTAR_PRIMIS_VIDEO_ENABLED);
  const isOutstreamVideoAdEnabled = useExperiment(OUTSTREAM_VIDEO_AD_ENABLED);

  // Prevents outstream video from showing on-page and in NP dialog at the same time
  if (isNowPlayingDialogOpen !== isInNowPlayingDialog) {
    return null;
  }

  if (!canShowAds || sponsorshipEnabled || !canShowNowPlayingMrecVideoAds) {
    return null;
  }

  if (isFreestarEnabled && isFreestarPrimisVideoEnabled && !isOutstreamVideoAdEnabled) {
    return <FreeStarVideoAdContainer center={center} isPlaceholder={isPlaceholder} />;
  }

  if (isOutstreamVideoAdEnabled) {
    return <OutstreamVideoAd id={id} center={center} isPlaceholder={isPlaceholder} />;
  }

  return null;
}
